import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Mail, Send, Plus, BarChart3, Users } from 'lucide-react';

// Toggle this to switch between mock data (true) and real backend (false)
const USE_MOCK_DATA = true;

const NewsletterDashboard: React.FC = () => {
  // Mock data for stats
  const totalCampaigns = 0;
  const totalSubscribers = 0;
  const totalSent = 0;
  const avgOpenRate = 0;

  const handleCreateNewsletter = () => {
    if (USE_MOCK_DATA) {
      alert('Newsletter creation backend not yet available. UI-only mode.');
    } else {
      // Navigate to newsletter creation page
    }
  };

  const handleManageSubscribers = () => {
    if (USE_MOCK_DATA) {
      alert('Subscriber management backend not yet available. UI-only mode.');
    } else {
      // Navigate to subscriber management
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Newsletter Dashboard</h2>
        <p className="text-muted-foreground">Overview of your newsletter campaigns</p>
      </div>

      {/* Quick Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Campaigns</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{totalCampaigns}</div>
            <p className="text-xs text-muted-foreground mt-1">Newsletter campaigns sent</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Subscribers</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{totalSubscribers}</div>
            <p className="text-xs text-muted-foreground mt-1">Active subscribers</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Emails Sent</CardTitle>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{totalSent}</div>
            <p className="text-xs text-muted-foreground mt-1">Total emails delivered</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Open Rate</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{avgOpenRate}%</div>
            <p className="text-xs text-muted-foreground mt-1">Email engagement</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Create Newsletter
            </CardTitle>
            <CardDescription>Design and send a new newsletter to your subscribers</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={handleCreateNewsletter}
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create New Newsletter
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Subscriber Management
            </CardTitle>
            <CardDescription>Manage your subscriber list and mailing lists</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={handleManageSubscribers}
              className="w-full"
              variant="outline"
            >
              <Users className="w-4 h-4 mr-2" />
              Manage Subscribers
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Newsletters</CardTitle>
          <CardDescription>Your latest newsletter campaigns</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No newsletters yet</p>
            <p className="text-sm">Start by creating your first newsletter campaign</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NewsletterDashboard;
